import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  Building2,
  Folder,
  Folders,
  GraduationCap,
  User,
  Users,
  X,
} from "lucide-react";
import {
  EnrollmentConfigState,
  EnrollmentTargetKind,
  EnrollmentTargetsState,
  EnrollmentTriggerPayload,
} from "./types";
import {
  buildInitialConfig,
  validateConfig,
} from "./config/enrollmentFieldsConfig";
import { openToast } from "@utils/toastStore";
import TargetSelectionStep from "./steps/TargetSelectionStep";
import ConfigurationStep from "./steps/ConfigurationStep";
import { useEnrollmentSubmit } from "./hooks/useEnrollmentSubmit";
import { useAttributesList } from "./hooks/useAttributesList";

interface EnrollmentModalProps {
  trigger: EnrollmentTriggerPayload;
  onClose: () => void;
}

type Step = "targets" | "config";

const initialTargets: EnrollmentTargetsState = {
  users: [],
  groups: [],
  initiatives: [],
};

const EnrollmentModal: React.FC<EnrollmentModalProps> = ({
  trigger,
  onClose,
}) => {
  const { t } = useTranslation("enrollment-modal");
  const theme = useTheme();

  const [step, setStep] = useState<Step>("targets");
  const [activeKind, setActiveKind] = useState<EnrollmentTargetKind>("users");
  const [targets, setTargets] =
    useState<EnrollmentTargetsState>(initialTargets);
  const [config, setConfig] =
    useState<EnrollmentConfigState>(buildInitialConfig);
  const [attemptedSubmit, setAttemptedSubmit] = useState(false);

  const { options: attributes } = useAttributesList(trigger.corporateId);
  const { submit, isSubmitting } = useEnrollmentSubmit();

  const totalSelectedCount =
    targets.users.length + targets.groups.length + targets.initiatives.length;
  const canProceedToConfig = totalSelectedCount > 0;

  const validation = useMemo(() => validateConfig(config), [config]);

  const handleTargetsChange = (
    kind: EnrollmentTargetKind,
    items: typeof targets.users,
  ) => setTargets((prev) => ({ ...prev, [kind]: items }));

  const handleSubmit = async () => {
    setAttemptedSubmit(true);
    if (!validation.valid) return;
    try {
      await submit({ trigger, targets, config });
      trigger.onSuccess?.();
      onClose();
      openToast({
        severity: "success",
        message: t("feedback.success") || "Iscrizione completata con successo",
      });
    } catch (e: any) {
      onClose();
      openToast({
        severity: "error",
        message:
          e?.message || t("feedback.error") || "Errore durante l'iscrizione",
      });
    }
  };

  const ObjectIcon =
    trigger.objectType === "course"
      ? Folder
      : trigger.objectType === "path"
        ? Folders
        : GraduationCap;

  return (
    <Dialog
      open
      onClose={isSubmitting ? undefined : onClose}
      fullWidth
      maxWidth="lg"
      PaperProps={{
        sx: {
          backgroundColor: theme.palette.background.default,
          borderRadius: "16px",
          border: `1px solid ${theme.palette.divider}`,
        },
      }}
    >
      <DialogTitle
        sx={{ padding: "20px 24px 0", color: theme.palette.text.primary }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            {trigger.objectTitle && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  color: theme.palette.text.primary,
                }}
              >
                <ObjectIcon size={16} />
                <Typography sx={{ fontSize: "13px" }}>
                  {trigger.objectTitle}
                </Typography>
              </Box>
            )}

            <Typography
              sx={{
                fontSize: "20px",
                fontWeight: 700,
                color: theme.palette.text.primary,
                mt: 0.5,
              }}
            >
              {t("title") || "Iscrivi"}
            </Typography>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 2,
                mt: 1,
                color: theme.palette.text.primary,
              }}
            >
              <Typography sx={{ fontSize: "13px" }}>
                {t("counter.prefix") || "Stai iscrivendo:"}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <User size={15} />
                <Typography sx={{ fontSize: "13px", fontWeight: 600 }}>
                  {targets.users.length} {t("counter.users") || "Utenti"}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Users size={15} />
                <Typography sx={{ fontSize: "13px", fontWeight: 600 }}>
                  {targets.groups.length} {t("counter.groups") || "Gruppi"}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Building2 size={15} />
                <Typography sx={{ fontSize: "13px", fontWeight: 600 }}>
                  {targets.initiatives.length}{" "}
                  {t("counter.initiatives") || "Iniziative"}
                </Typography>
              </Box>
            </Box>
          </Box>

          <IconButton
            onClick={onClose}
            aria-label="close"
            disabled={isSubmitting}
            sx={{ color: theme.palette.text.primary, flexShrink: 0 }}
          >
            <X size={20} />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent
        sx={{
          padding: "8px 24px 24px",
          "& .MuiOutlinedInput-root": { borderRadius: "8px" },
          "& .MuiOutlinedInput-notchedOutline": { borderRadius: "8px" },
        }}
      >
        {step === "targets" ? (
          <TargetSelectionStep
            trigger={trigger}
            activeKind={activeKind}
            onActiveKindChange={(k) => {
              setActiveKind(k);
            }}
            targets={targets}
            onTargetsChange={handleTargetsChange}
          />
        ) : (
          <ConfigurationStep
            trigger={trigger}
            config={config}
            onChange={setConfig}
            attributes={attributes}
            errors={attemptedSubmit ? validation.errors : {}}
            targets={targets}
            hiddenEnrollTypes={["AUTO", "BOTTOM"]}
          />
        )}
      </DialogContent>

      <DialogActions sx={{ padding: "12px 24px 20px" }}>
        {step === "config" && (
          <Button
            variant="outlined"
            onClick={() => setStep("targets")}
            disabled={isSubmitting}
            sx={{ textTransform: "none", borderRadius: "8px" }}
          >
            {t("actions.back") || "Indietro"}
          </Button>
        )}
        <Box sx={{ flex: 1 }} />
        {step === "targets" ? (
          <Button
            variant="contained"
            onClick={() => setStep("config")}
            disabled={!canProceedToConfig}
            sx={{ textTransform: "none", borderRadius: "8px", fontWeight: 700 }}
          >
            {t("actions.next") || "Avanti"}
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={isSubmitting}
            sx={{ textTransform: "none", borderRadius: "8px", fontWeight: 700 }}
          >
            {isSubmitting
              ? t("actions.submitting") || "Iscrizione in corso…"
              : t("actions.submit") || "Iscrivi"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default EnrollmentModal;
